import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Users, Trash2, StopCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const SHIFTS = [
  { value: "day",   label: "Day",   color: "bg-amber-100 text-amber-800" },
  { value: "swing", label: "Swing", color: "bg-orange-100 text-orange-800" },
  { value: "night", label: "Night", color: "bg-indigo-100 text-indigo-800" },
];
const EMPTY = { operator_name: "", operator_email: "", machine_id: "", work_order_id: "", shift_name: "day", notes: "" };

export default function GmesShifts() {
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("active");

  const load = () => api.get("/gmes/shifts").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/gmes/machines").then(({ data }) => setMachines(data)).catch(() => {});
    api.get("/gmes/work-orders").then(({ data }) => setWorkOrders(data)).catch(() => {});
  }, []);

  const machineMap = useMemo(() => Object.fromEntries(machines.map((m) => [m.id, m.name])), [machines]);
  const woMap = useMemo(() => Object.fromEntries(workOrders.map((w) => [w.id, `${w.wo_number} · ${w.product}`])), [workOrders]);
  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const active = items.filter((i) => i.status === "active");

  const save = async (e) => {
    e.preventDefault();
    if (!form.operator_name.trim()) { toast.error("Operator name required"); return; }
    setSaving(true);
    try {
      await api.post("/gmes/shifts", form);
      toast.success("Shift started");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const endShift = async (id) => {
    if (!window.confirm("End this shift?")) return;
    try {
      await api.patch(`/gmes/shifts/${id}/end`, {});
      toast.success("Shift ended");
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this shift entry?")) return;
    await api.delete(`/gmes/shifts/${id}`);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/shopfloor" className="hover:text-primary">Shop Floor</Link> / Shifts
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="gmes-shifts-title">
            Shift Board
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Who&rsquo;s running what right now. Start shifts, assign operators to machines and work orders.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-shift-btn">
          <Plus className="w-4 h-4 mr-2" /> Start shift
        </Button>
      </div>

      <div className="flex gap-1.5 mb-5">
        {[
          { v: "active", l: `Active · ${active.length}` },
          { v: "ended",  l: `Ended · ${items.length - active.length}` },
          { v: "all",    l: `All · ${items.length}` },
        ].map((t) => (
          <button key={t.v} onClick={() => setFilter(t.v)}
                  className={`rounded-sm px-3 py-1.5 text-xs font-mono-plex uppercase tracking-widest ${filter === t.v ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/70"}`}
                  data-testid={`shift-filter-${t.v}`}>
            {t.l}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <Users className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No {filter === "all" ? "" : filter} shifts</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Start a shift to assign an operator to a machine and (optionally) a work order.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => {
            const shift = SHIFTS.find((x) => x.value === s.shift_name) || SHIFTS[0];
            const active = s.status === "active";
            return (
              <div key={s.id} className={`border rounded-sm p-5 ${active ? "border-emerald-400 bg-emerald-50/50" : "border-border bg-card"}`} data-testid={`shift-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg leading-tight truncate">{s.operator_name}</div>
                    {s.operator_email && <div className="text-xs font-mono-plex text-muted-foreground mt-0.5 truncate">{s.operator_email}</div>}
                  </div>
                  <Badge className={`rounded-sm ${shift.color} hover:${shift.color} font-mono-plex text-[10px] uppercase tracking-widest`}>{shift.label}</Badge>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div><span className="text-muted-foreground text-xs font-mono-plex uppercase tracking-widest mr-2">Machine</span>{machineMap[s.machine_id] || "—"}</div>
                  <div><span className="text-muted-foreground text-xs font-mono-plex uppercase tracking-widest mr-2">WO</span>{woMap[s.work_order_id] || "—"}</div>
                </div>
                <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                  Started {s.start_time ? formatDistanceToNow(new Date(s.start_time), { addSuffix: true }) : "—"}
                  {s.end_time && <> · Ended {formatDistanceToNow(new Date(s.end_time), { addSuffix: true })}</>}
                </div>
                {s.notes && <p className="text-xs text-muted-foreground mt-2 italic">"{s.notes}"</p>}
                <div className="mt-3 flex items-center gap-2">
                  {active && (
                    <Button size="sm" variant="outline" onClick={() => endShift(s.id)} className="rounded-sm" data-testid={`end-shift-${s.id}`}>
                      <StopCircle className="w-3.5 h-3.5 mr-1.5" /> End shift
                    </Button>
                  )}
                  <button onClick={() => remove(s.id)} title="Delete" className="ml-auto p-1 text-muted-foreground hover:text-destructive hover:bg-red-50 rounded-sm">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-sm">
          <DialogHeader>
            <div className="overline text-accent">◉ NEW SHIFT</div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Assign operator</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Operator name *</label>
                <Input value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} className="rounded-sm" data-testid="shift-operator-input" required />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Email</label>
                <Input type="email" value={form.operator_email} onChange={(e) => setForm({ ...form, operator_email: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="overline text-muted-foreground block mb-1.5">Machine</label>
                <Select value={form.machine_id || "none"} onValueChange={(v) => setForm({ ...form, machine_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Shift</label>
                <Select value={form.shift_name} onValueChange={(v) => setForm({ ...form, shift_name: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{SHIFTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Work order (optional)</label>
              <Select value={form.work_order_id || "none"} onValueChange={(v) => setForm({ ...form, work_order_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {workOrders.filter((w) => w.status !== "complete" && w.status !== "cancelled").map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.wo_number} · {w.product}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm resize-none" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-shift-btn">
              {saving ? "Starting…" : "Start shift"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
